"use client";

import { Auth } from "@supabase/auth-ui-react";
import { I18nVariables, ThemeSupa, ViewType } from "@supabase/auth-ui-shared";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

import { useSupabase } from "@/app/supabase-provider";
import { EmailI18nVariables } from "@/types";
import { getURL } from "@/utils/helpers";

interface AuthUIProps {
    localization: I18nVariables;
    emailLocalization: EmailI18nVariables;
}

export default function AuthUI({
    localization,
    emailLocalization
}: AuthUIProps) {
    const t = useTranslations("Auth/Error");
    const { supabase } = useSupabase();
    const authRef = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();

    let view = searchParams.get("view") as ViewType;
    const returnTo = searchParams.get("returnTo");

    let redirectToURL;
    if (returnTo === decodeURIComponent("/#pricing")) {
        redirectToURL = `${getURL()}auth/callback?returnTo=${encodeURIComponent(
            "/#pricing"
        )}`;
    } else {
        redirectToURL = `${getURL()}auth/callback`;
    }

    console.log({ redirectToURL });

    if (view !== "sign_up") {
        view = "sign_in";
    }

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type !== "childList" ||
                    mutation.addedNodes.length === 0
                )
                    return;

                for (const node of mutation.addedNodes) {
                    if (
                        node instanceof HTMLElement &&
                        (node.classList.contains(
                            "supabase-account-ui_ui-message"
                        ) ||
                            node.classList.contains(
                                "supabase-auth-ui_ui-message"
                            ))
                    ) {
                        const originalMessage = node.innerHTML.trim();

                        let translatedMessage;
                        switch (originalMessage) {
                            case "To signup, please provide your email":
                            case "To signup, please provide your email or phone number":
                                translatedMessage = t("missing_email");
                                break;
                            case "Signup requires a valid password":
                                translatedMessage = t("missing_password");
                                break;
                            case "User already registered":
                                translatedMessage = t(
                                    "user_already_registered"
                                );
                                break;
                            case "Email link is invalid or has expired":
                                translatedMessage = t("email_link_expired");
                                break;
                            case "Token has expired or is invalid":
                                translatedMessage = t("token_expired");
                                break;
                            case "The new email address provided is invalid":
                                translatedMessage = t("new_email_invalid");
                                break;
                            case "Password should be at least 6 characters":
                                translatedMessage = t("password_length");
                                break;
                            case "Invalid login credentials":
                                translatedMessage = t(
                                    "invalid_login_credentials"
                                );
                                break;
                            case "Check your email for the password reset link":
                                translatedMessage = t(
                                    "check_email_password_reset"
                                );
                                break;
                            case "Check your email for the confirmation link":
                                translatedMessage = t(
                                    "check_email_confirmation"
                                );
                                break;
                        }

                        if (
                            !document.querySelector("#auth-forgot-password") &&
                            translatedMessage
                        ) {
                            node.innerHTML = translatedMessage;
                        }
                    }
                }
            });
        });

        if (authRef.current) {
            observer.observe(authRef.current, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div className="flex flex-col space-y-4" ref={authRef}>
            <Auth
                view={view}
                supabaseClient={supabase}
                additionalData={emailLocalization}
                providers={["google", "facebook"]}
                redirectTo={redirectToURL}
                appearance={{
                    theme: ThemeSupa,
                    variables: {
                        default: {
                            colors: {
                                brand: "#2563eb",
                                brandAccent: "#1d4ed8",
                                inputLabelText: "#374151",
                                defaultButtonText: "#374151",
                                inputPlaceholder: "#71717a",
                                anchorTextColor: "#374151",
                                anchorTextHoverColor: "#1f2937"
                            },
                            fontSizes: {
                                baseBodySize: "16px",
                                baseInputSize: "16px",
                                baseLabelSize: "16px",
                                baseButtonSize: "16px"
                            }
                        }
                    }
                }}
                localization={{ variables: localization }}
            />
        </div>
    );
}
